import { useEffect, useLayoutEffect, useState, useCallback, useMemo, useRef } from 'react';
import { useEffectOnActive } from 'keepalive-for-react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Play, ArrowCounterClockwise, Star, Heart, ShareNetwork, Headphones,
  PuzzlePiece, Ticket, Footprints, CaretRight, LockSimple, Trophy,
} from '@phosphor-icons/react';
import notify, { notifyIcons } from '../utils/notify';
import { formatDuration, formatRouteDistance, formatApproxDistance } from '../utils/measures';
import { plural } from '../utils/plural';
import BottomSheet from '../components/BottomSheet';
import Loader from '../components/Loader';
import RouteMapPreview from '../components/RouteMapPreview';
import MetroMark from '../components/MetroMark';
import useNavOffset from '../hooks/useNavOffset';
import {
  getRouteBySlug, getRouteSavers, addToSavedRoutes, removeFromSavedRoutes,
  resetRouteProgress,
} from '../services/api';
import telegramService from '../services/telegram';
import { useAuth } from '../context/AuthContext';
import { useCollections } from '../context/CollectionsContext';

// Сколько точек открыто на витрине, если редакция не задала своё число
const DEFAULT_PREVIEW_STOPS = 2;

const PLACES = ['место', 'места', 'мест'];
const TASKS = ['задание', 'задания', 'заданий'];

// Расчёт расстояния между двумя координатами (формула Хаверсина), км
function haversineKm(lat1, lon1, lat2, lon2) {
  const R = 6371;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLon = ((lon2 - lon1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLon / 2) ** 2;
  return R * 2 * Math.asin(Math.sqrt(a));
}

// Перевод км в пешеходное время (~5 км/ч)
function walkingMinutes(km) {
  return Math.round((km / 5) * 60);
}

// Вход бесплатный, если редакция поставила «0», — иначе это цена
const isPaidEntry = (fee) => fee != null && fee !== '' && fee !== '0' && fee !== 0;

// Миниатюра точки: своё фото точки, иначе первое фото места
const stopThumb = (stop) => stop.images?.[0]?.url || stop.place?.images?.[0]?.url || null;

/**
 * Переход к точке. Время из админки (`travelTime`, минуты от предыдущей
 * точки) — точное, его показываем как есть. Без него — прикидка по прямой
 * со знаком «~» (брендбук 6.2): «0,48 км» по прямой было ложной точностью
 */
function legText(from, to) {
  if (to.travelTime > 0) return `${to.travelTime} мин пешком`;
  const a = from.place;
  const b = to.place;
  if (a?.latitude == null || a?.longitude == null || b?.latitude == null || b?.longitude == null) return null;
  const km = haversineKm(a.latitude, a.longitude, b.latitude, b.longitude);
  return `~${walkingMinutes(km)} мин пешком · ${formatApproxDistance(km)}`;
}

// Круглая кнопка второстепенного действия — 44px, тап-зона по норме
const IconButton = ({ onClick, label, children }) => (
  <button
    onClick={onClick}
    aria-label={label}
    className="flex h-11 w-11 items-center justify-center rounded-full border-0 bg-theme-bg-secondary text-theme-text-primary transition-transform duration-instant ease-out active:scale-97"
    style={{ WebkitTapHighlightColor: 'transparent' }}
  >
    {children}
  </button>
);

// Чип «что внутри»: аудиогид, задания, билеты
const FeatureChip = ({ icon: Icon, children }) => (
  <span className="inline-flex h-8 items-center gap-1.5 rounded-full bg-theme-bg-secondary px-3 text-body-sm text-theme-text-primary">
    <Icon size={16} className="text-theme-text-secondary" />
    {children}
  </span>
);

/**
 * Витрина маршрута.
 *
 * Страница решает «иду / покупаю», а не ведёт по маршруту: описания точек,
 * подсказки, задания и аудио живут в режиме прогулки. Поэтому точки здесь
 * открыты не все — `previewStopsCount` задаёт редакция, остальные свёрнуты
 * в строку «Ещё N мест». У платного маршрута до покупки ещё и карта
 * размыта: виден район, но не путь.
 *
 * ⚠️ Скрытие здесь — только витрина. API пока отдаёт маршрут целиком,
 * закрыть содержимое платного маршрута нужно на бэкенде вместе с оплатой.
 */
const RouteDetailPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  // Признак «сохранено» выводится из контекста, а не хранится своей копией:
  // страница лежит в LRU-кеше, и запрос по [route, isAuthenticated]
  // при возврате не повторялся — кнопка показывала «Сохранено» после
  // удаления маршрута в «Избранном». То же чинилось в CollectionPage
  const { savedRoutes, addSavedRoute, removeSavedRoute, loadSaved } = useCollections();

  const [route, setRoute] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [savers, setSavers] = useState({ users: [], count: 0 });
  const [showRestartModal, setShowRestartModal] = useState(false);
  const [descExpanded, setDescExpanded] = useState(false);
  const [descClamped, setDescClamped] = useState(false);
  const descRef = useRef(null);
  // Индексы фактов, после которых строка переносится, — у них точка скрыта
  const factsRef = useRef(null);
  const [factBreaks, setFactBreaks] = useState(() => new Set());

  // Отступ под липкой панелью считается по её фактической высоте:
  // набор кнопок зависит от состояния маршрута
  const panelRef = useRef(null);
  // Высота нижнего меню — липкая панель кнопок встаёт над ним
  const navOffset = useNavOffset();
  const [panelHeight, setPanelHeight] = useState(0);

  const savedRecord = useMemo(
    () => savedRoutes.find(sr => sr.route?.id === route?.id) || null,
    [savedRoutes, route]
  );
  const isSaved = Boolean(savedRecord);

  const calcRouteState = useCallback(() => {
    const finished = !!localStorage.getItem(`route_timer_final_${slug}`);
    if (finished) return { isFinished: true, isInProgress: false, passed: 0 };
    const saved = localStorage.getItem(`route_progress_${slug}`);
    if (!saved) return { isFinished: false, isInProgress: false, passed: 0 };
    try {
      const { statuses } = JSON.parse(saved);
      const passed = Array.isArray(statuses)
        ? statuses.filter(s => s === 'done' || s === 'skipped').length
        : 0;
      return { isFinished: false, isInProgress: passed > 0, passed };
    } catch { return { isFinished: false, isInProgress: false, passed: 0 }; }
  }, [slug]);

  const [routeState, setRouteState] = useState(calcRouteState);
  const { isFinished, isInProgress, passed } = routeState;

  /**
   * Сброс прохождения. Источник истины — сервер, страница прогулки читает
   * его при загрузке. Без удаления серверной записи «Начать заново»
   * открывало экран финиша заново — прогресс возвращался с сервера.
   *
   * Ответ ждём: `navigate` ниже сразу монтирует прогулку, и она успевала
   * прочитать ещё не удалённую запись.
   */
  const resetRoute = async () => {
    localStorage.removeItem(`route_timer_final_${slug}`);
    localStorage.removeItem(`route_timer_start_${slug}`);
    localStorage.removeItem(`route_progress_${slug}`);
    if (route?.id) {
      await resetRouteProgress({ routeId: route.id });
    }
  };

  const startRoute = async () => {
    telegramService.hapticFeedback('medium');
    await resetRoute();
    localStorage.setItem(`route_timer_start_${slug}`, Date.now().toString());
    navigate(`/routes/${slug}/walk?t=${Date.now()}`);
  };

  const openWalk = () => {
    telegramService.hapticFeedback('medium');
    navigate(`/routes/${slug}/walk?t=${Date.now()}`);
  };

  // Оплата подключается отдельной задачей (PayKeeper)
  const buyRoute = () => {
    telegramService.hapticFeedback('light');
    notify('Покупка маршрутов скоро появится', { icon: notifyIcons.info });
  };

  const askRestart = () => {
    telegramService.hapticFeedback('light');
    setShowRestartModal(true);
  };

  useEffectOnActive(() => { setRouteState(calcRouteState()); }, [calcRouteState]);

  useEffect(() => { loadRoute(); }, [slug]); // eslint-disable-line
  useEffect(() => { if (route) loadSavers(); }, [route]); // eslint-disable-line

  // Панель появляется в разметке только после загрузки, поэтому наблюдатель
  // навешивается эффектом по тем же признакам, что решают её отрисовку
  useEffect(() => {
    const node = panelRef.current;
    if (!node) return;
    const measure = () => setPanelHeight(node.offsetHeight);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(node);
    return () => observer.disconnect();
  }, [loading, error, route, isInProgress, isFinished]);

  // «Читать полностью» — только если текст действительно обрезан
  useLayoutEffect(() => {
    const node = descRef.current;
    if (node && !descExpanded) setDescClamped(node.scrollHeight > node.clientHeight + 1);
  }, [route, descExpanded]);

  // Где строка фактов переносится, видно только после раскладки: сравниваем
  // вертикальное положение соседних значений. Пересчёт — при смене ширины
  useLayoutEffect(() => {
    const node = factsRef.current;
    if (!node) return;
    const measure = () => {
      const items = node.querySelectorAll('[data-fact]');
      const next = new Set();
      for (let i = 0; i < items.length - 1; i++) {
        if (items[i + 1].offsetTop > items[i].offsetTop) next.add(i);
      }
      setFactBreaks(prev => (
        prev.size === next.size && [...next].every(i => prev.has(i)) ? prev : next
      ));
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(node);
    return () => observer.disconnect();
  }, [route, loading]);

  // Тихое обновление списка при возврате: правки с этого устройства уже
  // в контексте, но сохранённое могло измениться с другого
  useEffectOnActive((active) => {
    if (active && isAuthenticated) loadSaved({ background: true });
  }, [isAuthenticated, loadSaved]); // eslint-disable-line

  const loadRoute = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await getRouteBySlug(slug);
      if (response.data?.length > 0) setRoute(response.data[0]);
      else setError('Маршрут не найден');
    } catch { setError('Ошибка загрузки данных'); }
    finally { setLoading(false); }
  };

  const loadSavers = async () => {
    try {
      const response = await getRouteSavers(route.id);
      setSavers(response.data || { users: [], count: 0 });
    } catch {}
  };

  const handleSave = async () => {
    if (!isAuthenticated) { notify.error('Войдите, чтобы сохранить маршрут'); return; }
    telegramService.hapticFeedback('medium');
    try {
      if (savedRecord) {
        await removeFromSavedRoutes(savedRecord.id);
        // Правка на месте: счётчик в профиле меняется сразу, без перезапроса
        removeSavedRoute(savedRecord.id);
        notify('Убрано из сохранённых', { icon: notifyIcons.favoriteRemoved });
      } else {
        const response = await addToSavedRoutes(route.id);
        // Ответ create не populate-ит связь, а признак ищется по `sr.route.id`
        addSavedRoute({ ...response.data, route });
        notify('Маршрут сохранён', { icon: notifyIcons.favoriteAdded });
      }
    } catch (err) {
      // 409 «уже сохранён» и 404 «записи нет» означают, что список разошёлся
      // с сервером — перечитываем вместо сообщения об ошибке
      const status = err.response?.status;
      if (status === 409 || status === 404) {
        loadSaved({ background: true });
        notify('Список обновлён', { icon: notifyIcons.info });
      } else {
        notify.error('Не удалось сохранить. Проверьте связь');
      }
    }
  };

  const handleShare = () => {
    telegramService.hapticFeedback('light');
    const shareUrl = `https://t.me/spbneformal_app_bot/apps?startapp=route-${route.slug}`;
    const shareText = `Посмотри этот маршрут: ${route.title}`;
    telegramService.openTelegramLink(
      `https://t.me/share/url?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(shareText)}`
    );
  };

  if (loading) return <Loader />;
  if (error || !route) return (
    <div className="flex min-h-screen items-center justify-center bg-theme-bg px-4">
      <div className="text-center">
        <p className="mb-6 text-body text-theme-text-secondary">{error || 'Маршрут не найден'}</p>
        <button
          onClick={() => navigate(-1)}
          className="h-12 rounded-md border-0 bg-theme-card-secondary px-[18px] text-label text-action-text transition-transform duration-fast ease-out active:scale-98"
          style={{ WebkitTapHighlightColor: 'transparent' }}
        >
          Назад
        </button>
      </div>
    </div>
  );

  const sortedStops = (route.stops || [])
    .filter(s => s.place)
    .sort((a, b) => a.order - b.order);
  const stopsCount = sortedStops.length;

  // Доступ к содержимому. Пока оплаты нет, платный маршрут закрыт для всех;
  // с PayKeeper сюда добавится проверка покупки
  const hasAccess = !route.isPaid;
  const isLocked = route.isPaid && !hasAccess;

  // Все точки видны тому, кто уже идёт или прошёл маршрут; остальным —
  // превью из `previewStopsCount`
  const revealAll = hasAccess && (isInProgress || isFinished);
  const previewCount = revealAll
    ? stopsCount
    : Math.min(stopsCount, Math.max(0, route.previewStopsCount ?? DEFAULT_PREVIEW_STOPS));
  const openStops = sortedStops.slice(0, previewCount);
  const hiddenStops = sortedStops.slice(previewCount);

  // Что внутри маршрута — то, что продаёт его до старта
  const audioCount = sortedStops.filter(s => s.audio).length;
  const taskCount = sortedStops.filter(s => s.task).length;
  const ticketCount = sortedStops.filter(s => isPaidEntry(s.entryFee)).length;

  const startMetro = sortedStops[0]?.place?.metro;

  const durationText = formatDuration(route.duration);
  const distanceText = formatRouteDistance(route.distance);
  const facts = [
    durationText,
    distanceText,
    stopsCount > 0 ? `${stopsCount} ${plural(stopsCount, PLACES)}` : null,
  ].filter(Boolean);

  const saverCount = savers.count || 0;
  const saverUsers = savers.users || [];
  const saverLabel = plural(saverCount, ['человек сохранил', 'человека сохранили', 'человек сохранили']);
  // Подложки аватаров без фото — декоративный ряд от бренд-шкалы
  const fallbackColors = ['var(--color-action)', 'var(--brand-700)', 'var(--brand-400)', 'var(--brand-800)', 'var(--brand-300)'];

  const priceText = route.price > 0 ? `${route.price.toLocaleString('ru-RU')} ₽` : null;

  return (
    <div
      className="min-h-screen bg-theme-bg"
      // До первого замера — запас по высоте панели с нижним меню
      style={{ paddingBottom: (panelHeight || navOffset + 90) + 24 }}
    >
      {/* ── Обложка 4:5 ──
          Вертикальная, как карточки мест. Во всю ширину окна: приложение
          живёт в колонке 448px (`max-w-md` в App.js), и в окне шире неё —
          браузер, Telegram Desktop — по краям фото оставались белые полосы.
          Обложка выходит за колонку, остальная страница — нет.
          На широком окне 4:5 вышло бы выше экрана — высота ограничена.
          Градиент только в нижней части, под заголовком, — фото
          не затемняется целиком (брендбук 1) */}
      <div
        className="relative overflow-hidden bg-theme-card-secondary"
        style={{
          width: '100vw',
          marginLeft: 'calc(50% - 50vw)',
          aspectRatio: '4 / 5',
          maxHeight: 'calc(var(--tg-viewport-height, 100vh) * 0.75)',
        }}
      >
        {route.image?.url && (
          <img src={route.image.url} alt="" className="h-full w-full object-cover" />
        )}
        <div
          className="absolute inset-0"
          style={{ background: 'linear-gradient(to bottom, rgba(0,0,0,0) 50%, rgba(0,0,0,.7) 100%)' }}
        />

        {/* Подпись — по колонке приложения, как текст ниже, а не по краю окна */}
        <div className="absolute inset-x-0 bottom-5 mx-auto max-w-md px-4">
          <div className="mb-2 flex flex-wrap items-center gap-2">
            {/* Лайм — акцент бренда, только с тёмным текстом (2.4). Цвет
                литералом: `text-primary` в тёмной теме светлеет */}
            <span className="rounded-sm bg-lime px-2 py-1 text-kicker text-[#16181D]">
              Маршрут редакции
            </span>
            {isLocked && (
              <span
                className="inline-flex items-center gap-1 rounded-sm px-2 py-1 text-kicker text-white"
                style={{ background: 'rgba(0,0,0,.45)' }}
              >
                <LockSimple size={12} weight="bold" />
                Платный
              </span>
            )}
            {isFinished && (
              <span className="rounded-sm bg-success px-2 py-1 text-kicker text-on-semantic">
                Пройден
              </span>
            )}
          </div>
          {/* Белый в обеих темах: заголовок лежит поверх фото */}
          <h1 className="m-0 font-display text-h1 text-white">{route.title}</h1>
        </div>
      </div>

      <div className="mx-auto max-w-app">

        {/* ── Факты одной строкой и действия справа ──
            Вместо четырёх плашек: иерархию держит текст, а не рамки (брендбук 1).
            «Поделиться» и «Сохранить» — здесь, а не на фото: в верхнем углу
            обложки они терялись на светлом снимке и спорили с кнопками
            самого Telegram в полноэкранном режиме */}
        <div className="flex items-center gap-3 px-4 pt-4">
          <div ref={factsRef} className="flex min-w-0 flex-1 flex-wrap items-center gap-x-2 gap-y-1 text-body text-theme-text-primary tabular">
            {/* Точка-разделитель стоит в конце значения. Если следующее
                значение перенеслось на новую строку, точка скрывается:
                ни в конце строки, ни в начале следующей она не нужна.
                Скрывается через `invisible`, место остаётся — иначе строка
                перестроилась бы и перенос мог бы исчезнуть */}
            {facts.map((f, i) => (
              <span key={f} data-fact className="flex items-center gap-2 whitespace-nowrap">
                {f}
                {(i < facts.length - 1 || route.rating > 0) && (
                  <span
                    className={`text-theme-text-hint ${factBreaks.has(i) ? 'invisible' : ''}`}
                    aria-hidden="true"
                  >
                    ·
                  </span>
                )}
              </span>
            ))}
            {route.rating > 0 && (
              <span data-fact className="flex items-center gap-1 whitespace-nowrap">
                <Star size={16} weight="fill" aria-label="Оценка" />
                {route.rating.toFixed(1).replace('.', ',')}
              </span>
            )}
          </div>
          <div className="flex shrink-0 gap-2">
            <IconButton onClick={handleShare} label="Поделиться маршрутом">
              <ShareNetwork size={20} />
            </IconButton>
            <IconButton onClick={handleSave} label={isSaved ? 'Убрать из сохранённых' : 'Сохранить маршрут'}>
              <Heart
                size={20}
                weight={isSaved ? 'fill' : 'regular'}
                className={isSaved ? 'text-theme-favorite-text' : undefined}
              />
            </IconButton>
          </div>
        </div>

        {/* ── Что внутри ── */}
        {(audioCount > 0 || taskCount > 0 || ticketCount > 0) && (
          <div className="flex flex-wrap gap-2 px-4 pt-3">
            {audioCount > 0 && <FeatureChip icon={Headphones}>Аудиогид</FeatureChip>}
            {taskCount > 0 && (
              <FeatureChip icon={PuzzlePiece}>{taskCount} {plural(taskCount, TASKS)}</FeatureChip>
            )}
            {ticketCount > 0 && (
              <FeatureChip icon={Ticket}>Билеты в {ticketCount} {plural(ticketCount, ['месте', 'местах', 'местах'])}</FeatureChip>
            )}
          </div>
        )}

        {/* ── Описание ── */}
        {route.description && (
          <div className="px-4 pt-5">
            <p
              ref={descRef}
              className={`m-0 whitespace-pre-line text-body text-theme-text-primary ${descExpanded ? '' : 'line-clamp-4'}`}
            >
              {route.description}
            </p>
            {descClamped && !descExpanded && (
              <button
                onClick={() => setDescExpanded(true)}
                className="mt-1 h-11 border-0 bg-transparent p-0 text-label text-action-text"
              >
                Читать полностью
              </button>
            )}
          </div>
        )}

        {/* ── Карта ── */}
        {stopsCount > 0 && (
          <div className="px-4 pt-6">
            <RouteMapPreview stops={sortedStops} blurred={isLocked} />
            {startMetro && (
              <div className="mt-3 flex items-center gap-2 text-body-sm text-theme-text-secondary">
                <MetroMark />
                <span>Старт у метро <span className="text-theme-text-primary">{startMetro}</span></span>
              </div>
            )}
          </div>
        )}

        {/* ── Точки: таймлайн ── */}
        {stopsCount > 0 && (
          <section className="px-4 pt-8">
            <h2 className="mb-4 font-display text-h2 text-theme-text-primary">Что вас ждёт</h2>

            <ol className="m-0 list-none p-0">
              {openStops.map((stop, index) => {
                const place = stop.place;
                const thumb = stopThumb(stop);
                const next = sortedStops[index + 1];
                const isLastOpen = index === openStops.length - 1;
                const hasLine = !isLastOpen || hiddenStops.length > 0;
                const leg = next ? legText(stop, next) : null;
                const meta = [
                  stop.audio && 'Аудио',
                  stop.task && 'Задание',
                  isPaidEntry(stop.entryFee) && stop.entryFee,
                ].filter(Boolean);

                return (
                  <li key={stop.id} className="flex gap-3">
                    {/* Колонка таймлайна: номер и линия до следующей точки */}
                    <div className="flex w-7 shrink-0 flex-col items-center">
                      <div className="mt-6 flex h-7 w-7 items-center justify-center rounded-full bg-action text-kicker text-on-action tabular">
                        {index + 1}
                      </div>
                      {hasLine && <div className="mt-1 w-0.5 flex-1 rounded-full bg-theme-border" />}
                    </div>

                    <div className="min-w-0 flex-1">
                      <button
                        onClick={() => { telegramService.hapticFeedback('light'); navigate(`/place/${place.slug}`); }}
                        className="flex w-full items-center gap-3 border-0 bg-transparent p-0 text-left transition-transform duration-instant ease-out active:scale-98"
                        style={{ WebkitTapHighlightColor: 'transparent' }}
                      >
                        <div className="w-[60px] shrink-0 overflow-hidden rounded-md bg-theme-card-secondary" style={{ aspectRatio: '3 / 4' }}>
                          {thumb && <img src={thumb} alt="" loading="lazy" className="h-full w-full object-cover" />}
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="line-clamp-2 font-display text-h3 text-theme-text-primary">{place.title}</div>
                          {place.shortDescription && (
                            <div className="mt-0.5 line-clamp-1 text-body-sm text-theme-text-secondary">
                              {place.shortDescription}
                            </div>
                          )}
                          {meta.length > 0 && (
                            <div className="mt-1 text-caption text-theme-text-secondary">{meta.join(' · ')}</div>
                          )}
                        </div>
                        <CaretRight size={20} className="shrink-0 text-theme-text-hint" />
                      </button>

                      {/* Переход к следующей точке. У скрытой точки время
                          тоже показываем — оно не раскрывает, куда идти */}
                      {leg && (
                        <div className="flex items-center gap-1.5 py-3 text-caption text-theme-text-secondary tabular">
                          <Footprints size={16} />
                          {leg}
                        </div>
                      )}
                      {!leg && hasLine && <div className="h-4" />}
                    </div>
                  </li>
                );
              })}

              {/* Свёрнутые точки: сколько ещё и когда откроются */}
              {hiddenStops.length > 0 && (
                <li className="flex gap-3">
                  <div className="flex w-7 shrink-0 flex-col items-center">
                    <div className="mt-6 flex h-7 w-7 items-center justify-center rounded-full bg-theme-bg-secondary text-theme-text-secondary">
                      <LockSimple size={16} weight="bold" />
                    </div>
                  </div>
                  <div className="flex min-w-0 flex-1 items-center gap-3">
                    <div className="flex shrink-0">
                      {hiddenStops.slice(0, 3).map((stop, i) => {
                        const thumb = stopThumb(stop);
                        return (
                          <div
                            key={stop.id}
                            className="w-[44px] overflow-hidden rounded-md border-2 border-theme-bg bg-theme-card-secondary"
                            style={{ aspectRatio: '3 / 4', marginLeft: i > 0 ? -16 : 0 }}
                          >
                            {thumb && (
                              <img
                                src={thumb}
                                alt=""
                                loading="lazy"
                                className="h-full w-full scale-110 object-cover"
                                style={{ filter: 'blur(5px)' }}
                              />
                            )}
                          </div>
                        );
                      })}
                    </div>
                    <div className="min-w-0">
                      <div className="font-display text-h3 text-theme-text-primary">
                        {previewCount > 0 && 'Ещё '}{hiddenStops.length} {plural(hiddenStops.length, PLACES)}
                      </div>
                      <div className="text-body-sm text-theme-text-secondary">
                        {isLocked ? 'Откроются после покупки' : 'Откроются на прогулке'}
                      </div>
                    </div>
                  </div>
                </li>
              )}
            </ol>
          </section>
        )}

        {/* ── Кто сохранил ── */}
        {saverCount > 0 && (
          <div className="flex items-center gap-3 px-4 pt-8">
            <div className="flex">
              {saverUsers.map((user, i) => (
                <div
                  key={user.id}
                  className="flex h-7 w-7 shrink-0 items-center justify-center overflow-hidden rounded-full border-2 border-theme-bg text-kicker text-white"
                  style={{ background: fallbackColors[i % fallbackColors.length], marginLeft: i > 0 ? -8 : 0 }}
                >
                  {user.photoUrl
                    ? <img src={user.photoUrl} alt="" className="h-full w-full object-cover" />
                    : user.firstName?.charAt(0)?.toUpperCase() || '?'}
                </div>
              ))}
            </div>
            <span className="text-body-sm text-theme-text-secondary tabular">
              {saverCount} {saverLabel} этот маршрут
            </span>
          </div>
        )}
      </div>

      {/* ── Липкая панель: одно основное действие ──
          Сохранение переехало сердцем на обложку, результат и «заново» —
          квадратной кнопкой рядом. Отступ снизу — реальная высота нижнего
          меню, а не константа: с системной врезкой Android меню выше 90px,
          и кнопки уходили под него */}
      <div
        ref={panelRef}
        className="fixed bottom-0 left-0 right-0 z-sticky border-t border-theme-border bg-theme-card"
        style={{ padding: `12px 16px ${navOffset + 12}px` }}
      >
        <div className="mx-auto flex max-w-app gap-2">
          {isLocked ? (
            <PrimaryButton onClick={buyRoute}>
              {priceText ? `Купить за ${priceText}` : 'Купить маршрут'}
            </PrimaryButton>
          ) : isInProgress ? (
            <>
              <PrimaryButton onClick={openWalk}>
                <Play size={20} weight="fill" />
                <span className="tabular">Продолжить · {passed} из {stopsCount}</span>
              </PrimaryButton>
              <SecondaryButton onClick={askRestart} label="Начать заново">
                <ArrowCounterClockwise size={22} />
              </SecondaryButton>
            </>
          ) : isFinished ? (
            <>
              <PrimaryButton onClick={askRestart}>
                <ArrowCounterClockwise size={20} />
                Пройти заново
              </PrimaryButton>
              <SecondaryButton onClick={openWalk} label="Посмотреть результат">
                <Trophy size={22} />
              </SecondaryButton>
            </>
          ) : (
            <PrimaryButton onClick={startRoute}>
              <Play size={20} weight="fill" />
              Начать маршрут
            </PrimaryButton>
          )}
        </div>
      </div>

      {/* ── Модалка «Начать заново» ── */}
      <BottomSheet
        isOpen={showRestartModal}
        onClose={() => setShowRestartModal(false)}
        title="Начать заново?"
        // Необратимое действие: тап мимо закрыл бы панель слишком легко
        closeOnBackdrop={false}
        footer={
          <div className="flex gap-2">
            <button
              onClick={() => setShowRestartModal(false)}
              className="flex-1 h-12 rounded-md text-label transition-colors duration-fast ease-out active:scale-98"
              style={{
                background: 'var(--color-card-secondary)',
                color: 'var(--color-text-secondary)',
              }}
            >
              Отмена
            </button>
            <button
              onClick={() => { setShowRestartModal(false); startRoute(); }}
              className="flex-1 h-12 rounded-md text-label border-0 transition-colors duration-fast ease-out active:scale-98"
              style={{ background: 'var(--color-danger)', color: 'var(--color-on-semantic)' }}
            >
              Начать заново
            </button>
          </div>
        }
      >
        <p className="text-body-sm text-theme-text-secondary">
          Весь прогресс и результат маршрута будут сброшены. Это действие нельзя отменить.
        </p>
      </BottomSheet>
    </div>
  );
};

const PrimaryButton = ({ onClick, children }) => (
  <button
    onClick={onClick}
    className="flex h-14 flex-1 items-center justify-center gap-2 rounded-md border-0 bg-action text-label text-on-action transition-transform duration-fast ease-out active:scale-98"
    style={{ WebkitTapHighlightColor: 'transparent' }}
  >
    {children}
  </button>
);

// Второстепенное действие — квадрат рядом с основным, без заливки акцентом
// (одно действие — один акцент, брендбук 1)
const SecondaryButton = ({ onClick, label, children }) => (
  <button
    onClick={onClick}
    aria-label={label}
    className="flex h-14 w-14 shrink-0 items-center justify-center rounded-md border-0 bg-theme-card-secondary text-theme-text-primary transition-transform duration-fast ease-out active:scale-98"
    style={{ WebkitTapHighlightColor: 'transparent' }}
  >
    {children}
  </button>
);

export default RouteDetailPage;
